import { useState } from "react"
import dynamic from "next/dynamic"
import { InstantSearch, Configure } from "react-instantsearch-dom"
import Icon from "@mdi/react"
import { mdiFilter } from "@mdi/js"
import {
	Paper,
	Grid,
	Container,
	Box,
	Hidden,
	Dialog,
	DialogActions,
	DialogContent,
} from "@mui/material"

import useAlgoliaURLSync from "@secureo/common/hooks/useAlgoliaURLSync"
import useToggle from "@secureo/common/hooks/useToggle"
import useTranslation from "hooks/useTranslation"

import SEO from "components/SEO"
import Button from "components/Buttons/Button"
import Information from "../IntermediateCategoryPage/Information"
import FiltersSideBar from "components/Search/Filters/FiltersSidebar"
import ListView from "components/Search/Results/ListView"
import GridView from "components/Search/Results/GridView"
import NumberOfResults from "components/Search/Results/NumberOfResults"
import ToggleResultsView from "components/Search/Results/ToggleResultsView"
import SortResults from "components/Search/Results/SortResults"
const Pagination = dynamic(() => import("../CategoryPage/Pagination"), {
	ssr: false,
})
import Help from "../CategoryPage/Help"
import NoResults from "./NoResults"

import { makeStyles } from "makeStyles"
import searchClient from "utils/algolia/searchClient"
import { categoryPageFilters } from "config/filterConfig"

const useStyles = makeStyles()((theme) => ({
	paper: {
		padding: theme.spacing(0),
	},
	sectionMobile: {
		[theme.breakpoints.down("md")]: {
			display: "none",
		},
	},
	configRoot: {
		flexGrow: 1,
	},
	divider: {
		marginTop: "20px",
	},
	mobileSticky: {
		position: "sticky",
		top: "0px",
		background: "#F7FAFA",
		width: "100%",
		zIndex: 2,
	},
	"@media only screen and (min-width: 768.98px)": {
		mobileSticky: {
			position: "relative",
			zIndex: 0,
		},
	},
}))

const hitsPerPageConfig = {
	listView: {
		defaultRefinement: 16,
		items: [
			{ value: 16, label: "16" },
			{ value: 18, label: "18" },
			{ value: 27, label: "29" },
		],
	},
	gridView: {
		defaultRefinement: 16,
		items: [
			{ value: 16, label: "16" },
			{ value: 18, label: "18" },
			{ value: 24, label: "24" },
		],
	},
}

const SearchPageContainer = ({
	indexName,
	resultsState,
	initialSearchState = {},
	onSearchParameters,
	widgetsCollector,
}: Props) => {
	const { classes } = useStyles()
	const [isListView, toggleIsListView] = useToggle(true)
	const [open, setOpen] = useState(false)
	const { t } = useTranslation("category")
	const asPath = `/search`
	const [searchState, onSearchStateChange] = useAlgoliaURLSync(
		initialSearchState,
		"/search",
		asPath,
	)

	const handleClickOpen = () => {
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
	}

	const selectedHistPerPageConfig = isListView
		? hitsPerPageConfig.listView
		: hitsPerPageConfig.gridView

	const sortingConfig = {
		defaultRefinement: indexName,
		options: [
			{
				value: indexName,
				label: t("mostSold"),
			},
			{
				value: `${indexName}_price_asc`,
				label: t("priceAscending"),
			},
			{
				value: `${indexName}_price_desc`,
				label: t("priceDescending"),
			},
		],
	}

	const hasResults = true

	const { query } = searchState

	return (
		<>
			<SEO pageType="search" asPath={asPath} />
			{hasResults ? (
				<>
					<Box my={5}>
						<Container>
							<InstantSearch
								indexName={indexName}
								searchClient={searchClient}
								resultsState={resultsState}
								searchState={searchState}
								onSearchParameters={onSearchParameters}
								onSearchStateChange={onSearchStateChange}
								widgetsCollector={widgetsCollector}
							>
								<Configure query={searchState.query || ""} />
								{query && (
									<h1 style={{ marginTop: 0 }}>
										{t("resultsFor")} &quot;{query}&quot;
									</h1>
								)}
								<Grid container spacing={3}>
									<Grid item xs={12} md={3}>
										<div className={classes.sectionMobile}>
											<Paper className={classes.paper}>
												<FiltersSideBar filters={categoryPageFilters} />
											</Paper>
											<Information />
										</div>
									</Grid>
									<Grid item xs={12} md={9}>
										<div className={classes.configRoot}>
											<Box>
												<div className={classes.mobileSticky}>
													<Grid container spacing={3}>
														<Hidden mdUp>
															<Grid item xs={6}>
																<Box mt={1}>
																	<Button
																		color="cart"
																		style={{ height: "40px" }}
																		onClick={handleClickOpen}
																	>
																		<Icon
																			path={mdiFilter}
																			size={0.8}
																		/>{" "}
																		{t("toFilter")}
																	</Button>
																	<Dialog
																		open={open}
																		keepMounted
																		onClose={handleClose}
																		aria-labelledby="alert-dialog-slide-title"
																		aria-describedby="alert-dialog-slide-description"
																	>
																		<DialogContent
																			style={{
																				padding: "0",
																				margin: "0",
																			}}
																		>
																			<FiltersSideBar
																				filters={
																					categoryPageFilters
																				}
																			/>
																		</DialogContent>
																		<DialogActions>
																			<Button
																				onClick={
																					handleClose
																				}
																				color="primary"
																			>
																				{t("cancel")}
																			</Button>
																			<Button
																				onClick={
																					handleClose
																				}
																				color="primary"
																			>
																				{t("toFilter")}
																			</Button>
																		</DialogActions>
																	</Dialog>
																</Box>
															</Grid>
														</Hidden>
														<SortResults
															defaultRefinement={
																sortingConfig.defaultRefinement
															}
															items={sortingConfig.options}
														/>

														<ToggleResultsView
															toggleIsListView={toggleIsListView}
														/>
														<NumberOfResults
															defaultRefinement={
																selectedHistPerPageConfig.defaultRefinement
															}
															items={selectedHistPerPageConfig.items}
														/>
													</Grid>
												</div>
												<Grid item xs={12}>
													<Box mt={2}>
														{isListView ? (
															<ListView categoryName="Suche" />
														) : (
															<GridView categoryName="Suche" />
														)}
													</Box>
												</Grid>
												<Grid item xs={12}>
													<Pagination />
												</Grid>
											</Box>
										</div>
									</Grid>
								</Grid>
							</InstantSearch>
							<Help />
						</Container>
					</Box>
				</>
			) : (
				<NoResults />
			)}
		</>
	)
}

interface Props {
	indexName: string
	resultsState: any
	initialSearchState: any
	asPath: string
	onSearchParameters?: () => any
	widgetsCollector?: any
}

export default SearchPageContainer
